import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../store/auth-store';
import { Button } from '@/components/ui/button';
import { useToast } from '@/lib/hooks/use-toast';
import { UserRole } from '@/types';

/**
 * A qué empresa entrás.
 *
 * Sólo aparece cuando hay más de una: con una sola, quedarse mirando una
 * pantalla con un único botón es una traba, no una elección.
 *
 * No se usa un desplegable: son pocas opciones y todas importan. Un botón
 * grande por empresa se lee de un vistazo y se acierta con el mouse sin
 * apuntar.
 */
export function PortalPickerPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const portales = useAuthStore((s) => s.portales);
  const elegirPortal = useAuthStore((s) => s.elegirPortal);
  const refrescarPortales = useAuthStore((s) => s.refrescarPortales);
  const user = useAuthStore((s) => s.user);
  const esDeLaPlataforma = user?.role === UserRole.PLATFORM_ADMIN;
  const [entrando, setEntrando] = useState<string | null>(null);
  const [cargando, setCargando] = useState(portales.length === 0);
  const [errorAlCargar, setErrorAlCargar] = useState(false);

  useEffect(() => {
    /*
      `portales` no se persiste (dura lo que dura la pestaña, a propósito): un
      F5 en esta misma pantalla, o llegar acá escribiendo la URL a mano, la
      deja vacía ANTES de que nadie la haya pedido. Sin esto, ese vacío
      transitorio se leía igual que "no tenés ninguna empresa asignada".
    */
    if (portales.length > 0) {
      setCargando(false);
      return;
    }

    /*
      🔴 Sin lista y sin sesión no hay de dónde sacarla: `/auth/portals` está
      detrás de `authenticate`, y el token de selección del ingreso no se
      persiste ni serviría para esa ruta. Desde que el ingreso dejó de abrir
      sesión, éste es el estado de CADA recarga de esta pantalla y de cada
      visita directa a `/portales` — antes no, porque el ingreso dejaba una
      sesión abierta y el `refrescarPortales()` de abajo alcanzaba.

      La única salida honesta es volver a pedir la contraseña. Quedarse acá
      mostrando «no tenés ninguna empresa asignada» le echaría la culpa a sus
      permisos de algo que es, simplemente, una pantalla sin credencial: es la
      misma confusión entre "falló" y "está vacío" que esta pantalla ya
      arregló para el pedido de red. `/portales` es una ruta pública
      (`router/index.tsx`), así que `<ProtectedRoute>` no corre acá y
      `<PortalGuard>` tampoco: no hay nadie más que pueda decidirlo.
    */
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelado = false;
    setErrorAlCargar(false);
    refrescarPortales()
      .catch(() => {
        // Sin este `catch`, un pedido que falla (red, 500) terminaba
        // igual que una lista vacía de verdad: "no tenés ninguna
        // empresa asignada" — la misma confusión entre "falló" y "está
        // vacío" que Important 5 le sacó al guardián, acá en el picker.
        if (!cancelado) setErrorAlCargar(true);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [isAuthenticated, portales.length, refrescarPortales, navigate]);

  const entrar = async (slug: string) => {
    setEntrando(slug);
    try {
      await elegirPortal(slug);
      // Recarga completa a propósito: el `basename` se resuelve al montar el
      // router, así que cambiar de empresa sin recargar dejaría el router
      // apuntando a la anterior.
      window.location.href = `/${slug}/dashboard`;
    } catch (err) {
      const detalle = (err as { response?: { data?: { error?: string } } } | undefined)?.response
        ?.data?.error;
      toast({
        title: 'No se pudo entrar',
        description: detalle ?? 'Inténtalo de nuevo.',
        variant: 'destructive',
      });
      setEntrando(null);
    }
  };

  if (cargando) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-muted-foreground text-sm">Cargando tus empresas…</span>
      </div>
    );
  }

  if (errorAlCargar) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 px-6">
        <h1 className="text-xl font-semibold">No se pudieron cargar tus empresas</h1>
        <p className="text-muted-foreground text-sm">
          Puede ser un problema de conexión. Prueba de nuevo.
        </p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (portales.length === 0) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 px-6">
        <h1 className="text-xl font-semibold">No tienes ninguna empresa asignada</h1>
        <p className="text-muted-foreground text-sm">
          Pídele a quien administra la plataforma que te dé acceso a una empresa.
        </p>
        <Button variant="outline" onClick={() => navigate('/login')}>
          Volver a ingresar
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-6">
      <div>
        <h1 className="text-2xl font-semibold">¿A qué empresa entras?</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Tienes acceso a {portales.length} empresas.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {portales.map((p) => (
          <button
            key={p.slug}
            type="button"
            disabled={entrando !== null}
            onClick={() => entrar(p.slug)}
            className="hover:bg-accent flex items-center gap-3 rounded-lg border p-4 text-left transition-colors disabled:opacity-60"
          >
            {p.logoUrl ? (
              <img src={p.logoUrl} alt="" className="h-8 w-8 rounded object-contain" />
            ) : (
              <span className="bg-muted flex h-8 w-8 items-center justify-center rounded text-sm font-semibold">
                {p.name.charAt(0)}
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{p.name}</span>
              <span className="text-muted-foreground block text-xs">/{p.slug}</span>
            </span>
            {entrando === p.slug && <Loader2 className="h-4 w-4 animate-spin" />}
          </button>
        ))}
      </div>

      {esDeLaPlataforma && (
        <>
          <div className="my-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">o</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/*
            🔴 El enlace se ofrece SÓLO con sesión abierta, y `isAuthenticated`
            es exactamente el predicado que mira `<ProtectedRoute>` para dejar
            pasar a `/plataforma`. Ésa es la garantía: si el enlace está, la
            puerta abre.

            Recién ingresado no hay sesión —`login` no la abre a propósito: la
            abre el canje del portal, y el token que trae el ingreso es de
            selección, que `authenticate` rechaza—, así que acá el enlace
            mandaba de vuelta al formulario de contraseña que la persona
            acababa de completar bien. Era intermitente, además, porque
            `isAuthenticated` se persiste: en un navegador donde el superadmin
            nunca cerró sesión el valor rehidratado hacía que pareciera andar.

            Se prefiere no ofrecer la puerta a ofrecer una que rebota. Y en vez
            de callarse, la pantalla dice por dónde se entra: desde el menú de
            la cuenta, ya adentro de un portal.
          */}
          {isAuthenticated ? (
            /*
              Separada de los portales a proposito. Entrar al panel y entrar a una
              empresa son dos cosas distintas, y la pantalla tiene que decirlo antes
              de que la persona elija — no despues.
            */
            <Link
              to="/plataforma"
              className="banda-plataforma flex items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-accent"
            >
              <ShieldCheck className="h-5 w-5 shrink-0 text-plataforma" aria-hidden="true" />
              <span>
                <span className="block font-semibold">Panel de plataforma · DATEC</span>
                <span className="block text-xs text-muted-foreground">
                  Administrar todas las empresas
                </span>
              </span>
            </Link>
          ) : (
            <div className="banda-plataforma flex items-center gap-3 rounded-lg border border-dashed border-border p-4">
              <ShieldCheck className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span>
                <span className="block font-medium">Panel de plataforma · DATEC</span>
                <span className="block text-xs text-muted-foreground">
                  Entra primero a una empresa; después lo abres desde tu menú de arriba a la
                  derecha.
                </span>
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PortalPickerPage;
